/**
 * SPRINT 8 — SEO par défaut de TOUTES les routes publiques (M10/M15).
 *
 * AVANT : 8 pages publiques n'avaient aucun SEOHead → title générique unique,
 * aucune meta description, canonical statique de index.html FAUX sur toutes
 * les routes sauf la home (duplicate content côté crawlers).
 *
 * PRINCIPE : monté dans LocalizedPublicLayout (parent), ce composant pose des
 * défauts corrects (title, description, canonical, hreflang) pour la route
 * courante. Les pages qui rendent leur propre <SEOHead> (enfants du layout)
 * GAGNENT automatiquement (react-helmet-async : le composant le plus profond
 * a priorité). Aucun risque de double canonical.
 */
import { useLocation } from 'react-router-dom';
import SEOHead from '../SEOHead';
import { useHasPageSeoOwner } from './SeoOwnership';

// slug FR -> { en, titleFr, titleEn, descFr, descEn, noindex? }
const ROUTES = {
  '': {
    en: '',
    titleFr: 'Wealth Management — Global Real Assets · Maison d’investissement en actifs réels',
    titleEn: 'Wealth Management — Global Real Assets · Private real assets investment house',
    descFr: "Maison d'investissement indépendante : opérations immobilières privées sélectionnées, structurées et administrées dans la durée, aux côtés d'investisseurs qualifiés.",
    descEn: 'Independent investment house: selected private real estate operations, structured and administered over time, alongside qualified investors.',
  },
  'a-propos': {
    en: 'about',
    titleFr: 'À propos — la maison',
    titleEn: 'About the house',
    descFr: "Douze années de pratique, une opération retenue sur vingt, un co-investissement systématique : la maison, ses convictions et sa discipline d'analyse.",
    descEn: 'Twelve years of practice, one operation retained out of twenty, systematic co-investment: the house, its convictions and its discipline.',
  },
  'notre-equipe': {
    en: 'our-team',
    titleFr: 'Notre équipe — les personnes derrière la maison',
    titleEn: 'Our team — the people behind the house',
    descFr: "Daniel Scortatore et Anissa Sire, cofondateurs, la présence en Asie du Sud-Est et l'organisation qui administre chaque opération : les personnes derrière la maison.",
    descEn: 'Co-founders Daniel Scortatore and Anissa Sire, the Southeast Asia presence and the organisation administering every operation: the people behind the house.',
  },
  'comment-ca-marche': {
    en: 'how-it-works',
    titleFr: 'Fonctionnement — cinq étapes, aucune précipitation',
    titleEn: 'How it works — five steps, no haste',
    descFr: "De la première conversation à la dernière distribution : comment une opération est sélectionnée, structurée en véhicule dédié, puis administrée par la maison.",
    descEn: 'From first conversation to final distribution: how an operation is selected, structured in a dedicated vehicle, then administered by the house.',
  },
  'journal': {
    en: 'journal',
    titleFr: 'Le Journal — carnets, convictions, chantiers',
    titleEn: 'The Journal — field notes, convictions, works',
    descFr: "La revue de la maison : réglementation, structuration, fiscalité et réalité des chantiers à Bali, Dubaï, en Andorre et au Portugal. Factuel, sourcé, daté.",
    descEn: 'The house review: regulation, structuring, tax and the reality of construction in Bali, Dubai, Andorra and Portugal. Factual, sourced, dated.',
  },
  'realisations': {
    en: 'track-record',
    titleFr: 'Réalisations — ce que nous avons construit',
    titleEn: 'Track record — what we have built',
    descFr: "Les opérations menées par la maison, présentées comme elles ont été vécues : contexte, structuration, chantier, exploitation. Sans chiffre inventé.",
    descEn: 'Operations led by the house, presented as they were lived: context, structuring, construction, operation. No invented figures.',
  },
  'contact': {
    en: 'contact',
    titleFr: 'Contact — engager la conversation',
    titleEn: 'Contact — start the conversation',
    descFr: "Une situation patrimoniale, un horizon, un projet : écrivez-nous. Chaque message est lu et reçoit une réponse personnelle sous 48 heures ouvrées.",
    descEn: 'A situation, a horizon, a project: write to us. Every message is read and receives a personal reply within 48 working hours.',
  },
  // ACQUISITION ENGINE V1 — funnel porteur de projet (fallback layout ;
  // la page rend son propre SEOHead via SeoOwnership).
  'porteurs-de-projet': {
    en: 'project-owners',
    titleFr: 'Présenter un projet — WM Global Real Assets',
    titleEn: 'Present a project — WM Global Real Assets',
    descFr: "Vous structurez une opération immobilière ? Présentez votre projet à la maison d'investissement : chaque dossier reçoit une réponse motivée.",
    descEn: 'Structuring a real estate operation? Present your project to an investment house: every file receives a substantiated answer.',
  },
  'demander-acces': {
    en: 'request-access',
    titleFr: "Demander l'accès — investisseurs qualifiés",
    titleEn: 'Request access — qualified investors',
    descFr: "L'accès aux opérations de la maison est réservé aux investisseurs qualifiés. Étude du profil, entretien, vérifications : le parcours d'entrée, sans précipitation.",
    descEn: 'Access to the house operations is reserved for qualified investors. Profile review, interview, verifications: the entry path, without haste.',
  },
  'creer-spv': {
    en: 'create-spv',
    titleFr: 'Créer une SPV — structuration & administration',
    titleEn: 'Create an SPV — structuring & administration',
    descFr: "Nous créons le véhicule, nous l'administrons, nous le clôturons : structuration juridique, conformité, tenue comptable et sécurité des flux, en tarification transparente.",
    descEn: 'We create the vehicle, administer it and close it: legal structuring, compliance, accounting and flow security, with transparent pricing.',
    // HARDENING V1 (LOT 4) — offre SPV masquée (LOT 402) : robots.txt la
    // Disallow déjà ; le meta robots doit dire la même chose (avant :
    // index,follow ici CONTRE noindex,nofollow posé par PublicSpvDisabledPage).
    noindex: true,
  },
  'creer-spv/intake': {
    en: 'create-spv/intake',
    titleFr: 'Créer une SPV — votre projet',
    titleEn: 'Create an SPV — your project',
    descFr: "Décrivez votre opération : notre équipe de structuration vous répond sous 48 heures ouvrées.",
    descEn: 'Describe your operation: our structuring team replies within 48 working hours.',
    noindex: true,
  },
  'connexion': {
    en: 'login',
    titleFr: 'Connexion — espace investisseur',
    titleEn: 'Sign in — investor area',
    descFr: "Accédez à votre espace investisseur privé.",
    descEn: 'Access your private investor area.',
    noindex: true,
  },
  'mentions-legales': {
    en: 'legal-notice',
    titleFr: 'Mentions légales',
    titleEn: 'Legal notice',
    descFr: "Informations juridiques de l'éditeur du site Wealth Management — Global Real Assets.",
    descEn: 'Legal information of the publisher of the Wealth Management — Global Real Assets website.',
    // CLOTURE SEO/GEO — le corps des pages légales n'existe qu'en FRANÇAIS
    // (fallback composant + CMS non traduits). Tant que la traduction EN
    // n'est pas produite : pas de paire hreflang déclarée (l'équivalent EN
    // n'existe pas RÉELLEMENT) et la variante EN est noindex VOLONTAIRE
    // (pattern LOT 402). Retirer ce flag dès la traduction publiée.
    enUntranslated: true,
  },
  'politique-de-confidentialite': {
    en: 'privacy-policy',
    titleFr: 'Politique de confidentialité',
    titleEn: 'Privacy policy',
    descFr: "Comment la maison collecte, protège et utilise vos données personnelles.",
    descEn: 'How the house collects, protects and uses your personal data.',
    enUntranslated: true,
  },
  'politique-de-cookies': {
    en: 'cookie-policy',
    titleFr: 'Politique de cookies',
    titleEn: 'Cookie policy',
    descFr: "L'usage des cookies sur ce site : finalités, durées, consentement et refus, à tout moment.",
    descEn: 'Cookie usage on this site: purposes, durations, consent and refusal, at any time.',
    enUntranslated: true,
  },
  'conditions-generales': {
    en: 'terms-of-use',
    titleFr: 'Conditions générales',
    titleEn: 'Terms of use',
    descFr: "Les conditions d'utilisation du site et de l'espace investisseur.",
    descEn: 'Terms of use of the website and the investor area.',
    enUntranslated: true,
  },
  'avertissement-risques': {
    en: 'risk-disclosure',
    titleFr: 'Avertissement sur les risques',
    titleEn: 'Risk disclosure',
    descFr: "Tout investissement en actifs réels comporte des risques, dont la perte partielle ou totale du capital et une liquidité limitée.",
    descEn: 'Any real assets investment carries risks, including partial or total loss of capital and limited liquidity.',
    enUntranslated: true,
  },
};

// Résolution inverse EN -> FR pour construire les hreflangs depuis une URL EN.
const EN_TO_FR = Object.fromEntries(
  Object.entries(ROUTES).map(([fr, cfg]) => [cfg.en, fr]),
);

export default function PublicSeoDefaults() {
  const { pathname } = useLocation();
  // HARDENING V1 (LOT 4) — si la page courante rend son propre <SEOHead>
  // (enregistré via SeoOwnership), les défauts du layout se taisent :
  // react-helmet-async ne dédupliquant pas entre instances, deux SEOHead
  // simultanés exposaient deux meta descriptions aux moteurs.
  const hasPageOwner = useHasPageSeoOwner();
  const [, lang, ...rest] = pathname.split('/');
  const slug = rest.join('/').replace(/\/+$/, '');
  if (hasPageOwner) return null;

  let frSlug = null;
  if (lang === 'fr' && slug in ROUTES) frSlug = slug;
  if (lang === 'en' && slug in EN_TO_FR) frSlug = EN_TO_FR[slug];
  if (frSlug === null) return null; // routes dynamiques (journal/:slug, réalisations/:slug, p/:slug) gèrent leur propre SEO

  const cfg = ROUTES[frSlug];
  const frPath = frSlug ? `/fr/${frSlug}` : '/fr';
  const enPath = cfg.en ? `/en/${cfg.en}` : '/en';
  const isFr = lang === 'fr';
  const canonicalPath = isFr ? frPath : enPath;
  // CLOTURE SEO/GEO — contenu EN non traduit (pages légales) : pas de paire
  // hreflang (l'équivalent EN n'existe pas réellement) et variante EN en
  // noindex VOLONTAIRE tant que la traduction n'est pas publiée.
  const enUntranslated = Boolean(cfg.enUntranslated);
  const hreflangs = enUntranslated
    ? { fr: frPath }
    : { fr: frPath, en: enPath };

  return (
    <SEOHead
      title={isFr ? cfg.titleFr : cfg.titleEn}
      description={isFr ? cfg.descFr : cfg.descEn}
      canonical={`https://www.wealth-management-assets.com${canonicalPath}`}
      hreflangs={hreflangs}
      noindex={Boolean(cfg.noindex) || (enUntranslated && !isFr)}
      isLayoutDefault
    />
  );
}
